/**
 * Session controls: session status, recording indicator, mic mode switch and the mic toggle
 * (push-to-talk) — plan §5.1 "Voice capture — open mic / toggle".
 */
import type { MicMode } from './recorder';
import type { SessionStatus } from '../session/store';

export interface SessionControlsProps {
  status: SessionStatus;
  micMode: MicMode;
  micState: string;
  segments: number;
  chunks: number;
  busy: boolean;
  error: string | null;
  canStart: boolean;
  canEnd: boolean;
  onStart: () => void;
  onEnd: () => void;
  onNewSession: () => void;
  onToggleMic: () => void;
  onModeChange: (mode: MicMode) => void;
  onAdvance: () => void;
}

export function SessionControls({
  status,
  micMode,
  micState,
  segments,
  chunks,
  busy,
  error,
  canStart,
  canEnd,
  onStart,
  onEnd,
  onNewSession,
  onToggleMic,
  onModeChange,
  onAdvance,
}: SessionControlsProps) {
  const recording = micState === 'recording';
  const finished = status === 'transcribing' || status === 'structuring' || status === 'review' || status === 'rendering';

  return (
    <section className="rounded-lg border border-chalk-200 bg-white/70 p-5 shadow-sm">
      <h2 className="mb-3 text-lg font-semibold tracking-tight">Session</h2>

      <div className="flex flex-wrap items-center gap-3 text-sm">
        <span className="flex items-center gap-2">
          <span
            data-testid="recording-indicator"
            data-recording={recording ? 'true' : 'false'}
            aria-hidden="true"
            className={`inline-block h-3 w-3 rounded-full ${
              recording ? 'animate-pulse bg-red-500' : 'bg-chalk-200'
            }`}
          />
          <span data-testid="mic-state" className="font-mono text-xs">
            {micState}
          </span>
        </span>
        <span className="text-charcoal-500">status</span>
        <span data-testid="session-status" className="font-mono">
          {status}
        </span>
      </div>

      <div className="mt-3 flex flex-wrap gap-2">
        <button
          type="button"
          data-testid="session-start"
          disabled={!canStart || busy}
          onClick={onStart}
          className="rounded bg-copper px-3 py-1.5 text-sm font-medium text-charcoal disabled:opacity-40"
        >
          Start session
        </button>
        <button
          type="button"
          data-testid="session-end"
          disabled={!canEnd || busy}
          onClick={onEnd}
          className="rounded bg-charcoal px-3 py-1.5 text-sm font-medium text-chalk disabled:opacity-40"
        >
          End session
        </button>
        <button
          type="button"
          data-testid="session-advance"
          disabled={!finished || busy}
          onClick={onAdvance}
          className="rounded border border-charcoal px-3 py-1.5 text-sm font-medium disabled:opacity-40"
        >
          Analyse
        </button>
        <button
          type="button"
          data-testid="session-new"
          disabled={!(status === 'complete' || status === 'error') || busy}
          onClick={onNewSession}
          className="rounded border border-chalk-200 px-3 py-1.5 text-sm font-medium disabled:opacity-40"
        >
          New session
        </button>
      </div>

      <fieldset className="mt-4 rounded-lg border border-chalk-200 p-3">
        <legend className="px-1 text-sm font-medium">Mic mode</legend>
        <div className="flex gap-4 text-sm">
          <label className="flex items-center gap-2">
            <input
              type="radio"
              name="mic-mode"
              data-testid="mic-mode-open"
              checked={micMode === 'open'}
              onChange={() => onModeChange('open')}
            />
            Open mic
          </label>
          <label className="flex items-center gap-2">
            <input
              type="radio"
              name="mic-mode"
              data-testid="mic-mode-ptt"
              checked={micMode === 'push-to-talk'}
              onChange={() => onModeChange('push-to-talk')}
            />
            Toggle mic (push-to-talk)
          </label>
        </div>
      </fieldset>

      <div className="mt-4 flex items-center gap-3">
        <button
          type="button"
          data-testid="mic-toggle"
          data-mic-mode={micMode}
          disabled={status !== 'recording'}
          onClick={onToggleMic}
          className={`rounded px-3 py-1.5 text-sm font-medium ${
            recording ? 'bg-red-500 text-white' : 'bg-charcoal text-chalk'
          } disabled:opacity-40`}
        >
          {micMode === 'open'
            ? recording
              ? 'Pause mic'
              : 'Resume mic'
            : recording
              ? 'Stop segment'
              : 'Start segment'}
        </button>
        <span className="text-xs text-charcoal-500" data-testid="segment-count">
          {segments} {segments === 1 ? 'segment' : 'segments'} · {chunks} audio{' '}
          {chunks === 1 ? 'chunk' : 'chunks'} uploaded
        </span>
      </div>

      {error && (
        <p data-testid="session-error" role="alert" className="mt-3 text-sm text-copper-600">
          {error}
        </p>
      )}
    </section>
  );
}
